import { useEffect, useRef } from "react";
import { AFK_TIMEOUT } from "../utils/constants";

/**
 * Hook that detects user inactivity and refreshes the page after 75 seconds.
 * Only active when enabled (e.g., for multiplayer games).
 * Resets the timer when:
 * - User performs any action (mouse, keyboard, touch, scroll)
 * - User returns to the page after leaving (visibility change)
 */
export const useAfkDetection = (enabled: boolean) => {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        // Only run if enabled (for multiplayer games)
        if (!enabled) {
            return;
        }
        const resetTimer = () => {
            // Clear existing timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            // Set new timeout to refresh page after 75 seconds
            timeoutRef.current = setTimeout(() => {
                window.location.reload();
            }, AFK_TIMEOUT);
        };

        const handleVisibilityChange = () => {
            // Reset timer when user returns to the page
            if (!document.hidden) {
                resetTimer();
            }
        };

        // User activity events to track
        const activityEvents = [
            "mousedown",
            "mousemove",
            "keydown",
            "scroll",
            "touchstart",
            "click",
        ];

        // Add event listeners for user activity
        activityEvents.forEach((event) => {
            window.addEventListener(event, resetTimer);
        });

        // Add visibility change listener
        document.addEventListener("visibilitychange", handleVisibilityChange);

        // Start the timer initially
        resetTimer();

        // Cleanup on unmount
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            activityEvents.forEach((event) => {
                window.removeEventListener(event, resetTimer);
            });
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [enabled]);
};
